/**
 * CYP ADHD PAD — Strategies and Recommendations (section 2)
 *
 * Each prompt is one toggle, "Include in patient letter", written onto the
 * question's answer. A ticked topic adds that guidance paragraph to the
 * family's report via the {{IF:}} keys derived in cyp_car/pad_data.py.
 */
import React, { useEffect, useRef, useState, useMemo, useContext, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import PADSideNavbar from '../9_postAssessmentDiscussion/components/PADSideNavbar';
import { CheckCircleIcon, PlusIcon, CheckIcon } from '@heroicons/react/solid';
import InfoTooltip from '../../../components/InfoTooltip';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstGeneratedFields, buildFirstCompletedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { isPadAdhdOutcomePositive } from '../../../utils/conditionChecks';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import {
  getPadStageOrder,
  getPadSidebarIndex,
  getPadSidebarSections,
  getPadNavLinks,
  buildPadSidebarUnlockFlags,
  readIsCarryOutHealthChecksNow,
  rewriteSubsectionTitleDisplay,
} from './cypPadNavigationUtils';

export default function PADStrategiesAndRecommendations() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();
  const isPreviewMode = useIsPreviewMode();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const { isAgreedMedicationPlanEnabled, isPadTreatmentFirst = false, isAppointmentDatesEnabled = false } = useWorkflowConfig(patientDataModel, claims);

  // Determines whether this user is the clinician assigned to the PAD stage.
  const canEdit = useCanEditStage(patientDataModel, 'PAD', { previewMode: isPreviewMode });

  const [isNavigating, setIsNavigating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formState, setFormState] = useState({});
  const isInitialLoad = useRef(true);

  // Constants
  const currentStageTitle = 'CYP ADHD Post-Assessment Discussion';
  const currentSectionNumber = 2;
  const currentSectionTitle = 'Strategies and Recommendations';

  const sectionData = useMemo(() => (
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber] || {}
  ), [patientDataModel, currentSectionNumber]);
  const subsections = sectionData?.subsections || {};

  // Ordered stage list for the clinician PAD — controls sidebar order,
  // display numbers, and prev/next links.
  const padStageOrder = useMemo(
    () => getPadStageOrder({ isPadTreatmentFirst, isAgreedMedicationPlanEnabled }),
    [isPadTreatmentFirst, isAgreedMedicationPlanEnabled]
  );

  // Navigation links derived from the ordered stage list.
  const isCarryOutHealthChecksNow = useMemo(
    () => readIsCarryOutHealthChecksNow(patientDataModel),
    [patientDataModel]
  );
  const padNav = useMemo(
    () => getPadNavLinks(String(currentSectionNumber), padStageOrder, { isCarryOutHealthChecksNow }),
    [padStageOrder, isCarryOutHealthChecksNow]
  );

  // Sidebar active index follows the ordered stage list.
  const sidebarActiveIndex = useMemo(
    () => getPadSidebarIndex(padStageOrder, String(currentSectionNumber)),
    [padStageOrder]
  );

  // Diagnostic outcome controls sidebar unlock state.
  const isDiagnosisADHD = useMemo(
    () => isPadAdhdOutcomePositive(patientDataModel),
    [patientDataModel]
  );

  const isBeginningMedicationNow = useMemo(() => {
    const answer = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.['4']?.subsections?.['4']?.questions?.['2']?.answer?.value;
    return Array.isArray(answer) && answer.includes('Yes');
  }, [patientDataModel]);

  // Sidebar sections with unlock flags matching the adult PAD logic.
  const [sidebarSections, setSidebarSections] = useState([]);
  useEffect(() => {
    const sections = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections || {};
    setSidebarSections(
      getPadSidebarSections(
        sections,
        padStageOrder,
        buildPadSidebarUnlockFlags({
          patientDataModel,
          isPadTreatmentFirst,
          isDiagnosisADHD,
          isCarryOutHealthChecksNow,
          isBeginningMedicationNow,
        }),
      ),
    );
  }, [patientDataModel, isCarryOutHealthChecksNow, isBeginningMedicationNow, padStageOrder, isPadTreatmentFirst, isDiagnosisADHD]);

  // Mark the section as completed when the clinician navigates away.
  // Since there is no content to capture yet, completion simply sets the
  // status so the sidebar shows a tick and the stage does not block
  // downstream stages.
  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[currentSectionNumber];
    if (currentSectionData?.status === 'completed') return Promise.resolve();

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [currentSectionNumber]: {
            status: 'completed',
            progress: 100,
          },
        },
      },
    });

    return flushPendingUpdates({ noTimeout: true });
  }, [canEdit, patientDataModel, currentSectionNumber, patientId, updatePatientFirestore, flushPendingUpdates]);

  // Next-section navigation with completion.
  const handleNextSection = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${padNav.nextLink}`);
    });
  }, [isNavigating, patientId, navigate, padNav.nextLink, handleSectionCompletion]);

  // Previous-section navigation (no completion needed).
  const handlePrevSection = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    navigate(`/patient/${patientId}${padNav.prevLink}`);
  }, [isNavigating, patientId, navigate, padNav.prevLink]);

  // Sidebar rows report their index, matching PADSideNavbar. The route comes
  // off the ordered section list, not off a click argument.
  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = sidebarSections[index];
      if (section?.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, sidebarSections, handleSectionCompletion]);

  // Load saved letter choices once. Later Firestore echoes must not wipe a
  // toggle the clinician has just clicked.
  useEffect(() => {
    if (!isInitialLoad.current) return;
    if (!sectionData?.subsections) return;
    const initialFormState = {};
    Object.values(sectionData.subsections).forEach((subsection) => {
      Object.values(subsection?.questions || {}).forEach((question) => {
        if (question?.firestoreFieldName) {
          initialFormState[question.firestoreFieldName] = {
            value: question?.answer?.value || [],
          };
        }
      });
    });
    setFormState(initialFormState);
    isInitialLoad.current = false;
  }, [sectionData]);

  // Toggle "Include in patient letter" for one prompt and save it on that
  // question. The value array is what a future {{IF:}} block will read.
  const handleLetterToggle = async (subsectionId, fieldName, option, isSelected) => {
    if (!canEdit) return;
    const previous = formState[fieldName]?.value || [];
    const nextValue = isSelected
      ? previous.filter((choice) => choice !== option)
      : [...previous, option];
    setFormState((prev) => ({ ...prev, [fieldName]: { value: nextValue } }));
    const questionIndex = Object.entries(sectionData.subsections?.[subsectionId]?.questions || {})
      .find(([, question]) => question.firestoreFieldName === fieldName)?.[0];
    if (!questionIndex) {
      setFormState((prev) => ({ ...prev, [fieldName]: { value: previous } }));
      return;
    }
    try {
      await updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [currentSectionNumber]: {
              subsections: {
                [subsectionId]: {
                  questions: {
                    [questionIndex]: { answer: { value: nextValue } },
                  },
                },
              },
            },
          },
        },
      });
    } catch (error) {
      console.error('Error saving letter choice:', error);
      setFormState((prev) => ({ ...prev, [fieldName]: { value: previous } }));
    }
  };

  // ── Finishing the PAD from this page ────────────────────────────────────
  // Strategies only shows "Finish" when it is the last reachable stage: in
  // the treatment-first order it owns the health-checks gate, and declining
  // health checks ends the PAD here (getPadNavLinks). No CYP org uses that
  // order today, but when it does this page must complete the module exactly
  // as Treatment Options (3_TRT) and Health Checks (4_HLT) do, otherwise the
  // PAD stage would never reach 'completed' and the overview/documents
  // dashboard would show it as still in progress.
  //
  // Completion is a two-step flow shared with the other PAD pages: Finish
  // marks this section complete and opens the confirmation dialog; confirming
  // stamps the module-level fields. The bridge (CypAdhdPadBridge) turns the
  // padReportGenerated stamp into the stage's 'completed' status.
  const handleFinish = useCallback(async () => {
    setIsSaving(true);
    try {
      await handleSectionCompletion();
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Error completing section:', error);
    } finally {
      setIsSaving(false);
    }
  }, [handleSectionCompletion]);

  // Confirmed completion: stamp the module as complete (padReportGenerated +
  // first-completed fields), write the audit entry and inferred appointment
  // date, then go to the documents dashboard. Mirrors 3_TRT / 4_HLT.
  const handleCompleteModule = useCallback(() => {
    setIsDialogOpen(false);

    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      },
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does
    // not write appointmentDates).
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/cyp-adhd/documents`);
  }, [patientDataModel, patientId, updatePatientFirestore, isAppointmentDatesEnabled, navigate]);

  // Counter text for the footer -- "2 of N" where N is the total number of
  // PAD stages. This page is always section 2 in the ordered stage list.
  const counterText = `${sidebarActiveIndex + 1} of ${padStageOrder.length}`;

  // Whether the forward action is a "Go to next stage" button vs a "Finish"
  // button (the latter only when this is the last reachable stage).
  const showNextStage = !!padNav.nextLink;

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-shrink-0">
          <Breadcrumb
            tupleList={[
              ['Patient Overview', `/patient/${patientId}/cyp-adhd`],
              [currentStageTitle],
              [currentSectionTitle],
            ]}
          />
        </div>
        <div className="flex flex-1 min-h-0">
          <PADSideNavbar
            sections={sidebarSections}
            currentSectionIndex={sidebarActiveIndex}
            navigateToSection={navigateToSection}
            isNavigating={isSaving || isNavigating}
          />
          <div className="flex-1 flex flex-col min-h-0">
            {isPreviewMode && <PreviewModeBanner />}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Section Title */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">{padNav.displayNumber}. {currentSectionTitle}</h2>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                A ticked topic adds that guidance to the family's report.
              </p>
              {Object.entries(subsections).map(([subsectionId, subsection]) => {
                if (!subsection?.questions || Object.keys(subsection.questions).length === 0) return null;
                return (
                  <div key={subsectionId} className="p-8 mb-8 bg-gray-50 rounded-2xl border border-gray-200">
                    <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                      {rewriteSubsectionTitleDisplay(subsection.subsectionTitleDisplay, padNav.displayNumber)}
                    </h3>
                    <div className="space-y-4">
                      {Object.entries(subsection.questions).map(([questionId, question]) => {
                        if (!question) return null;
                        const currentSelections = formState[question.firestoreFieldName]?.value || [];
                        return (
                          <div key={questionId} className="mb-4 flex items-start">
                            <div className="w-96 flex items-center gap-2">
                              {question.tooltipText ? (
                                <InfoTooltip content={question.tooltipText} />
                              ) : (
                                <div className="w-4 h-4" />
                              )}
                              <h4 className="text-left font-semibold text-gray-700">
                                {question.questionTitle}
                              </h4>
                            </div>
                            <ul className="ml-5 items-center flex flex-row gap-4">
                              {(question.options || []).map((option) => {
                                const isSelected = currentSelections.includes(option);
                                return (
                                  <li
                                    key={option}
                                    onClick={() => canEdit && handleLetterToggle(
                                      subsectionId,
                                      question.firestoreFieldName,
                                      option,
                                      isSelected,
                                    )}
                                    className={`
                                      flex-1 p-2 w-64 rounded-lg transition-all duration-300 ease-in-out
                                      text-center flex flex-row items-center justify-center gap-2
                                      ${!canEdit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer group'}
                                      ${isSelected
                                        ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                                        : canEdit
                                          ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                                          : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                                      }
                                    `}
                                  >
                                    <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                                      {isSelected ? <CheckIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
                                    </div>
                                    <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                      {option}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pinned footer navigation -- matches the CYP CI / Adult Autism
                modular proforma pattern. */}
            <ModularStepFooter
              showBack={!!padNav.prevLink}
              backLabel={`Back to ${padNav.prevTitle}`}
              onBack={handlePrevSection}
              backDisabled={isSaving || isNavigating}
              counterText={counterText}
              showNext={showNextStage}
              onNext={showNextStage ? handleNextSection : undefined}
              nextLabel={showNextStage ? `Go to ${padNav.nextTitle}` : undefined}
              nextDisabled={isNavigating}
              rightActions={
                !showNextStage && canEdit ? (
                  <button
                    onClick={handleFinish}
                    disabled={isSaving || isNavigating}
                    className={`flex items-center py-2 px-4 rounded-md shadow-sm transition-all duration-200
                      ${(isSaving || isNavigating)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary/90'}`}
                  >
                    <span className="font-semibold text-sm">
                      {isSaving ? 'Saving Progress...' : 'Finish Post-Assessment Discussion'}
                    </span>
                    {isSaving
                      ? <ButtonSpinner className="w-5 h-5 ml-2" />
                      : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* Completion confirmation -- same dialog as 3_TRT / 4_HLT. The CYP PAD
          has no standalone LLM report to generate, so confirming completes
          the module and goes straight to the documents dashboard. */}
      <ModuleCompletionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title="Post-Assessment Discussion Complete"
        width={600}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleCompleteModule} />
          </>
        }
      >
        <SummarySection title="Summary:">
          The Post-Assessment Discussion has been completed. Would you like to generate the report?
        </SummarySection>
      </ModuleCompletionDialog>
    </Layout>
  );
}
